// Profil › „Mitteilungen auf diesem Gerät“ (docs/mobile/SPEZIFIKATION.md §12): Push-Mitteilungen der App ein- und
// ausschalten, Kategorien wählen. Die Fachlogik liegt im Kern (`push.*` aus '@kernel'); Zeitpunkte sind ISO-Texte.
import { PERMISSION_AUTHENTICATED, defineContract } from '@shared/contract'
import { PUSH_CATEGORIES, PUSH_ENVIRONMENTS, PUSH_PLATFORMS, zPushToken } from '@shared/push'
import { z } from 'zod'

const zTimestamp = z.iso.datetime()

export const zPushCategoryPreference = z.object({
  category: z.enum(PUSH_CATEGORIES),
  title: z.string(),
  description: z.string(),
  enabled: z.boolean(),
  /** noch nie eingestellt – es gilt die Vorgabe */
  isDefault: z.boolean(),
})

export const profilePushSettings = defineContract({
  name: 'platform.profile.pushSettings',
  title: 'Eigene Push-Mitteilungen anzeigen',
  description:
    'Ob dieses Gerät Push-Mitteilungen bekommt, welche Arten eingeschaltet sind und ob der Server sie senden kann.',
  llm: {
    description:
      'Welche Push-Mitteilungen die angemeldete Person auf ihren Geräten bekommt (Freigaben, Aufgaben, Meldebestand, weitere Hinweise). ' +
      'Verwenden für „Bekomme ich Mitteilungen aufs Handy?“.',
    keywords: ['Push', 'Mitteilung', 'Handy', 'App', 'Benachrichtigung'],
  },
  input: z.strictObject({
    /** Token dieses Geräts (aus der App) – ohne Token nur Kategorien und Zahl der Geräte */
    deviceToken: zPushToken.optional(),
  }),
  output: z.object({
    /** Wege dieser Installation: Schlüssel für APNs je Umgebung, Dienstkonto für FCM */
    availability: z.object({ ios: z.object({ sandbox: z.boolean(), production: z.boolean() }), android: z.boolean() }),
    /** dieses Gerät (nur mit Token) */
    device: z.object({ registered: z.boolean(), lastSeenAt: zTimestamp.nullable() }).nullable(),
    /** angemeldete Geräte der Person in diesem Betrieb */
    deviceCount: z.int().nonnegative(),
    categories: z.array(zPushCategoryPreference),
  }),
  permission: PERMISSION_AUTHENTICATED,
  risk: 'read',
  assistant: 'never',
})

export const profileRegisterPushDevice = defineContract({
  name: 'platform.profile.registerPushDevice',
  title: 'Mitteilungen auf diesem Gerät einschalten',
  description:
    'Meldet das Gerät für Push-Mitteilungen an (oder frischt die Anmeldung auf). War es für eine andere Person angemeldet, gehört es danach Ihnen.',
  llm: { description: 'Meldet das Gerät der App für Push-Mitteilungen an. Nur für die App.', keywords: [] },
  input: z.strictObject({
    deviceToken: zPushToken,
    platform: z.enum(PUSH_PLATFORMS),
    /** APNs-Umgebung der App (Debug: sandbox, Store/TestFlight: production); Android immer production */
    environment: z.enum(PUSH_ENVIRONMENTS).default('production'),
    appVersion: z
      .string()
      .trim()
      .max(40)
      .regex(/^[0-9A-Za-z.+_() -]*$/, { error: 'Die App-Version hat eine unerwartete Form.' })
      .optional(),
    /** bisheriges Token desselben Geräts, wenn die App ein neues bekommen hat */
    previousDeviceToken: zPushToken.optional(),
  }),
  output: z.object({ registered: z.literal(true), deviceCount: z.int().nonnegative() }),
  permission: PERMISSION_AUTHENTICATED,
  risk: 'write',
  // persönlicher Zustand des Geräts ohne Geschäftsbezug; die App frischt die Anmeldung bei jedem Start auf
  audit: 'none',
  assistant: 'never',
})

export const profileUnregisterPushDevice = defineContract({
  name: 'platform.profile.unregisterPushDevice',
  title: 'Mitteilungen auf diesem Gerät ausschalten',
  description:
    'Meldet das Gerät von Push-Mitteilungen ab – auch, wenn es zuletzt für eine andere Person angemeldet war.',
  llm: { description: 'Meldet das Gerät der App von Push-Mitteilungen ab. Nur für die App.', keywords: [] },
  input: z.strictObject({ deviceToken: zPushToken }),
  output: z.object({ removed: z.boolean() }),
  permission: PERMISSION_AUTHENTICATED,
  risk: 'write',
  audit: 'none',
  assistant: 'never',
})

export const profileSetPushCategory = defineContract({
  name: 'platform.profile.setPushCategory',
  title: 'Art von Push-Mitteilungen ein- oder ausschalten',
  description:
    'Schaltet eine Art von Push-Mitteilungen (etwa Freigaben, Aufgaben, Meldebestand) für alle Ihre Geräte ein oder aus.',
  llm: { description: 'Schaltet eine Art eigener Push-Mitteilungen um. Nur für die Oberfläche.', keywords: [] },
  input: z.strictObject({ category: z.enum(PUSH_CATEGORIES), enabled: z.boolean() }),
  output: z.object({ category: z.enum(PUSH_CATEGORIES), enabled: z.boolean() }),
  permission: PERMISSION_AUTHENTICATED,
  risk: 'write',
  audit: 'none',
  assistant: 'never',
})
